import {
  ChangeEvent,
  FC,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

import { getApiUrl, getUploadUrl } from "@repo/edtech-api";
import {
  CrossIcon,
  DraftIcon,
} from "@repo/edtech-assets";
import { getFileSizeByHeadRequest } from "@repo/edtech-shared";
import {
  Loader,
  LoaderV2,
  ProgressLine,
} from "@repo/edtech-ui-kit";
import clsx from "clsx";

import {
  replaceBaseUrlForTools,
  validateAnswer,
} from "../../../../../../../../lib";
import { QuestionComponentProps, TestQuestionFrontType } from "../../../../../../../../model";
import { OriginAsset } from "../../../../../../../../origins";

import st from "./Open.module.scss";

type Props = QuestionComponentProps;

type UploadStatus = "idle" | "uploading" | "processing" | "error" | "too_large";

const MAX_FILE_SIZE_MB = 100;
const MAX_FILE_SIZE = MAX_FILE_SIZE_MB * 1024 * 1024;

export const Open: FC<Props> = ({
  setUserAnswer,
  userAnswer,
  isFileUpload,
  setFileAnswer,
  fileAnswer,
  error,
  setError,
  currentQuestion,
}) => {
  const [ progress, setProgress ] = useState(0);
  const [ uploadStatus, setUploadStatus ] = useState<UploadStatus>("idle");
  const [ uploadedFileName, setUploadedFileName ] = useState<string | null>(null);
  const [ uploadedFileSize, setUploadedFileSize ] = useState<number | null>(null);
  const [ isFileMetaLoading, setIsFileMetaLoading ] = useState(false);

  const xhrRef = useRef<XMLHttpRequest | null>(null);
  const uploadFilesRef = useRef<HTMLInputElement | null>(null);
  const justUploadedFileRef = useRef<string | null>(null);

  const isUploading = uploadStatus === "uploading" || uploadStatus === "processing";

  const fileUrl = getUploadUrl(fileAnswer);

  const onClickUploadFiles = useCallback(() => {
    uploadFilesRef.current?.click();
  }, []);

  const resetStates = useCallback(() => {
    setProgress(0);
    setUploadStatus("idle");
    setUploadedFileName(null);
    setUploadedFileSize(null);
    setFileAnswer("");
  }, [ setFileAnswer ]);

  const failUpload = useCallback(() => {
    resetStates();
    setUploadStatus("error");
  }, [ resetStates ]);

  const failTooLarge = useCallback(() => {
    resetStates();
    setUploadStatus("too_large");
  }, [ resetStates ]);

  const loadFile = (
    formData: FormData, name: string, size: number,
  ) => {
    const xhr = new XMLHttpRequest();
    xhrRef.current = xhr;

    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) {
        setProgress(Math.round((event.loaded / event.total) * 100));
      }
    };

    xhr.upload.onload = () => {
      setProgress(100);
      setUploadStatus("processing");
    };

    xhr.onload = () => {
      if (xhr.status === 413) {
        failTooLarge();
        return;
      }

      try {
        const result = JSON.parse(xhr.responseText);
        if (xhr.status < 400 && result?.status?.description) {
          justUploadedFileRef.current = result.status.description;
          setFileAnswer(result.status.description);
          setUploadedFileName(name);
          setUploadedFileSize(Math.round(size / 1024));
          setUploadStatus("idle");
        } else if (result?.status?.error === "error too big file") {
          failTooLarge();
        } else {
          failUpload();
        }
      } catch {
        failUpload();
      }
    };

    xhr.onerror = failUpload;
    xhr.onabort = resetStates;

    xhr.open("POST", replaceBaseUrlForTools(`${getApiUrl()}/tools/load`));
    xhr.send(formData);
  };

  const onChangeUploadFiles = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[ 0 ];
    if (!file) return;

    if (file.size > MAX_FILE_SIZE) {
      failTooLarge();
      e.target.value = "";
      return;
    }

    const formData = new FormData();
    formData.append("uploadfile", file);
    setUploadedFileName(file.name);
    setProgress(0);
    setUploadStatus("uploading");
    loadFile(
      formData, file.name, file.size,
    );

    e.target.value = "";
  };

  const cancelUpload = () => {
    xhrRef.current?.abort();
  };

  useEffect(() => {
    if (!fileAnswer || !isFileUpload) return;
    if (justUploadedFileRef.current === fileAnswer) return;

    setIsFileMetaLoading(true);

    const start = Date.now();

    const rawName = decodeURIComponent(fileAnswer.split("/").pop() || "");
    const match = rawName.match(/^[^_]+_(.+)$/);
    setUploadedFileName(match?.[ 1 ] || rawName);

    getFileSizeByHeadRequest(fileUrl, "kb")
      .then((size) => {
        setUploadedFileSize(size);
      })
      .catch(() => {
        setUploadedFileSize(null);
      })
      .finally(() => {
        const elapsed = Date.now() - start;
        const minSkeletonTime = 400;
        const remaining = minSkeletonTime - elapsed;

        if (remaining > 0) {
          setTimeout(() => setIsFileMetaLoading(false), remaining);
        } else {
          setIsFileMetaLoading(false);
        }
      });

  }, [ fileAnswer, isFileUpload ]);

  const handleChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    setUserAnswer(value);

    const {
      hasError,
      message,
    } = validateAnswer(
      value,
      TestQuestionFrontType.Open,
      currentQuestion?.validation,
      currentQuestion?.validationInfo,
    );

    setError({
      hasError,
      message,
    });
  };

  const hasFileInfo = !!fileAnswer && !!uploadedFileName;
  const uploadingLabel = uploadStatus === "processing" ?
    "Обработка" :
    "Загрузка";
  return (
    <div className={st.open_container}>
      <div className={st.field}>
        {
          error.message && (
            <span className={st.text_error}>{error.message}</span>
          )
        }
        <textarea
          className={clsx(st.textarea, error.hasError && st.error)}
          placeholder="Ваш ответ"
          value={userAnswer as string}
          onChange={handleChange}
        />
      </div>

      {
        isFileUpload && (
          <div className={st.upload}>
            <input
              ref={uploadFilesRef}
              className={st.file_input}
              type="file"
              onChange={onChangeUploadFiles}
            />
            <button
              className={st.upload_btn}
              disabled={isUploading}
              type="button"
              onClick={onClickUploadFiles}
            >
              <OriginAsset
                assetKey="openQuestionAttachFileIcon"
              />
              <span>Прикрепить файл (до {MAX_FILE_SIZE_MB} Мб)</span>
            </button>

            {
              isUploading && (
                <div
                  aria-live="polite"
                  className={st.loading_wrapper}
                  role="status"
                >
                  <LoaderV2
                    className={st.loading_spinner}
                    size={24}
                  />
                  <div className={st.loading}>
                    <ProgressLine percent={progress} />
                    <div className={st.loading_info}>
                      <span className={st.loading_name}>
                        {uploadingLabel}: {uploadedFileName ?? "Файл"}
                      </span>
                      {
                        uploadStatus === "uploading" && (
                          <span>{progress}%</span>
                        )
                      }
                    </div>
                  </div>
                  <button
                    aria-label="Отменить загрузку"
                    className={st.close_btn}
                    type="button"
                    onClick={cancelUpload}
                  >
                    <CrossIcon className={st.close_icon} />
                  </button>
                </div>
              )
            }

            {
              uploadStatus === "error" && (
                <span
                  className={st.text_error}
                  role="alert"
                >
                  Не удалось загрузить файл. Попробуйте еще раз
                </span>
              )
            }

            {
              uploadStatus === "too_large" && (
                <span
                  className={st.text_error}
                  role="alert"
                >
                  Не удалось загрузить файл: размер больше {MAX_FILE_SIZE_MB} Мб
                </span>
              )
            }

            {
              hasFileInfo && !isFileMetaLoading && !isUploading && (
                <div className={st.uploaded_files}>
                  <div className={st.file_wrapper}>
                    <DraftIcon />
                    <div className={st.file_info}>
                      <button
                        className={st.file_name}
                        onClick={
                          () =>
                            window.open(
                              fileUrl, "_blank", "noopener,noreferrer",
                            )
                        }
                      >
                        {uploadedFileName}
                      </button>
                      {
                        uploadedFileSize !== null && (
                          <span className={st.file_size}>{uploadedFileSize} КБ</span>
                        )
                      }
                    </div>
                    <button
                      className={st.close_btn}
                      onClick={resetStates}
                    >
                      <CrossIcon className={st.close_icon} />
                    </button>
                  </div>
                </div>
              )
            }

            {
              isFileMetaLoading && (
                <span className={st.loader}>
                  <Loader />
                </span>
              )
            }
          </div>
        )
      }
    </div>
  );
};
